import React, { useState, useEffect } from 'react';
import {
  Backdrop,
  Button,
  TextField,
  CircularProgress,
  Grid,
  Container,
  Typography
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import { disablePageScroll, enablePageScroll } from 'scroll-lock';
import Editor from 'react-simple-code-editor';
import prism from 'prismjs';

import Submission, { Test } from '../../models/Submission';
import TestOutputs from '../TestOutputs';
import { cleanCode } from '../../libs/cleaner';
import useSolutionGallery from '../../hooks/useSolutionGallery';
import useSpecComments from '../../hooks/useSpecComments';
import utils from '../../libs/utils';

interface InvestigateBackdropProps {
  visibility: boolean;
  onClose(): void;
}

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: '#ffffff',
    borderRadius: 3,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
  option: {
    border: 0,
  },
  paper: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    boxShadow: '0px 3px 4px -3px rgba(0,0,0,0.75)',
    MozBoxShadow: '0px 3px 4px -3px rgba(0,0,0,0.75)',
    WebkitBoxShadow: '0px 3px 4px -3px rgba(0,0,0,0.75)',
  },
  listbox: {
    borderTop: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
}));

export const InvestigateBackdrop = ({
                                      visibility,
                                      onClose,
                                    }: InvestigateBackdropProps) => {
  const classes = useStyles();
  const [isSelectActive, setSelectActive] = useState(false as boolean);
  const [options, setOptions] = useState([] as string[]);
  const [isFetchingSubmissions, setIsFetchingSubmissions] = useState(
    false as boolean
  );
  const [isFetchingSubmission, setIsFetchingSubmission] = useState(
    false as boolean
  );
  const [hasInitialized, setHasInitialized] = useState(false as boolean);
  const [errorMessage, setErrorMessage] = useState('' as string);
  const [submission, setSubmission] = useState(
    undefined as Submission | undefined
  );
  const [selectedSubmissionID, setSelectedSubmissionID] = useState(
    null as string | null
  );

  const {
    renderModal,
    renderSaveButton,
    renderOpenButton,
    renderSnackbar
  } = useSolutionGallery();

  const {
    renderModal: renderSpecCommentsModal,
    renderOpenButton: renderSpecCommentsOpenButton
  } = useSpecComments();

  const hideHandler = (event: any) => {
    setSubmission(undefined);
    setSelectedSubmissionID(null);
    onClose();
  };

  const overrideBackgroundClickEvent = (event: any) => event.stopPropagation();

  const handleActivateSelect = () => setSelectActive(true);

  const handleDeactivateSelect = () => setSelectActive(false);

  const handleChange = async (event: any, value: string | null) => {
    setSubmission(undefined);
    setSelectedSubmissionID(value);

    if (!!value) {
      try {
        setIsFetchingSubmission(true);

        const result = await Submission.get(value);

        setSubmission(result);
      } catch (e) {
        setErrorMessage(utils.getRequestErrorMessage(e));
      } finally {
        setIsFetchingSubmission(false);
      }
    }
  };

  const updateSubmission = (setter: (submission: Submission) => Submission) => {
    setSubmission(setter(JSON.parse(JSON.stringify(submission))));
  };

  const test = async (data: Submission) => {
    const submission = new Submission(data);

    try {
      updateSubmission((submission) => {
        submission.test.outputs = null;
        submission.test.isBusy = true;
        submission.test.errorMessage = '';
        return submission;
      });

      const outputs = await submission.submit();

      updateSubmission((submission) => {
        submission.test.outputs = outputs;
        submission.test.isBusy = false;
        return submission;
      });
    } catch (error) {
      updateSubmission((submission) => {
        submission.test.errorMessage = error.message;
        submission.test.isBusy = false;
        return submission;
      });
    }
  };

  const fetchSubmissions = async () => {
    try {
      setErrorMessage('');
      setHasInitialized(false);
      setSelectedSubmissionID(null);
      setOptions([]);
      setIsFetchingSubmissions(true);

      const response = await Submission.getAllIDs();

      setOptions(response);
    } catch (e) {
      setErrorMessage(utils.getRequestErrorMessage(e));
    } finally {
      setIsFetchingSubmissions(false);
      setHasInitialized(true);
    }
  };

  useEffect(() => {
    if (visibility) {
      disablePageScroll();
      if (!hasInitialized) {
        fetchSubmissions();
      }
    } else {
      enablePageScroll();
    }
  }, [visibility]);

  const TestResult = (props: { data: Test }) => {
    const outputs = props.data.outputs;
    const errorMessage = props.data.errorMessage;

    return (
      <>
        {errorMessage && (
          <div className="text-center p-4">
            <h3 className="m-0">An Error Occurred</h3>
            <p className="m-0">{errorMessage}</p>
            <p className="m-0">Please run test again.</p>
          </div>
        )}

        {outputs && !errorMessage && <TestOutputs outputs={outputs} />}
      </>
    );
  };

  return (
    <>
      { renderModal() }

      { renderSnackbar() }

      { renderSpecCommentsModal() }

      <Backdrop
        className={`${classes.backdrop} py-5 d-flex align-items-start`}
        open={visibility}
        onClick={hideHandler}
      >
        <Container>
          <Grid
            container
            spacing={3}
            className={classes.container}
            onClick={overrideBackgroundClickEvent}
          >

            <Grid item xs={12}>
              <Typography variant="h5">Investigate Submission</Typography>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                classes={{
                  option: classes.option,
                  listbox: classes.listbox,
                  paper: classes.paper,
                }}
                clearOnEscape
                value={selectedSubmissionID}
                open={isSelectActive}
                onOpen={handleActivateSelect}
                onClose={handleDeactivateSelect}
                onChange={handleChange}
                options={options}
                loading={isFetchingSubmissions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Submission"
                    variant="outlined"
                    placeholder="Enter submission ID"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {isFetchingSubmissions ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {(() => {

              if (errorMessage) return (
                <Grid item xs={12}>
                  <div className="text-center p-5">
                    <h4 className="m-0">Error Loading Data</h4>
                    <p className="m-0">{errorMessage}</p>
                    <Button
                      type="button"
                      onClick={fetchSubmissions}
                      color="primary"
                      className="mt-3"
                      variant="contained"
                    >
                      Try Again
                    </Button>
                  </div>
                </Grid>
              );

              if (isFetchingSubmission) return (
                <Grid item xs={12} className="text-center">
                  <CircularProgress color="primary" size={50} />
                </Grid>
              );

              if (submission) {
                const showSideInfo = !!(submission.test.errorMessage || submission.test.outputs);

                return (
                  <Grid item xs={12} className="text-center">
                    <Grid
                      container
                      spacing={2}
                    >
                      {/* editor */}
                      <Grid item xs={showSideInfo ? 6 : 12} className="d-flex flex-column">
                        <div
                          style={{ maxHeight: '65vh', overflow: 'auto' }}
                          className="flex-fill"
                        >
                          <Editor
                            value={submission.answer}
                            onValueChange={(answer) => {
                              updateSubmission((submission) => {
                                submission.answer = answer;
                                return submission;
                              });
                            }}
                            onBlur={(event: any) => {
                              updateSubmission((submission) => {
                                submission.answer = cleanCode(
                                  event.target.value
                                );
                                return submission;
                              });
                            }}
                            disabled={submission.test.isBusy}
                            highlight={(code) =>
                              prism.highlight(
                                code,
                                prism.languages.javascript,
                                'javascript'
                              )
                            }
                            padding={10}
                            style={{
                              fontFamily: '"Fira code", "Fira Mono", monospace',
                              backgroundColor: 'rgba(250,250,250, 1)',
                              fontSize: 12,
                            }}
                            textareaClassName="box-border"
                            preClassName="box-border"
                            autoFocus
                          />
                        </div>

                        <div className="mt-3 d-flex align-items-center justify-content-between">

                          <div>

                            { renderSaveButton({specId: submission.id, code: submission.answer}) }

                            { renderOpenButton({specId: submission.id}) }

                            {renderSpecCommentsOpenButton({specId: submission.id})}

                          </div>

                          <div>
                            <Button
                              type="button"
                              disabled={submission.test.isBusy}
                              onClick={() => test(submission)}
                              variant="contained"
                              color="primary"
                            >
                              {submission.test.isBusy && (
                                <CircularProgress size={14} className="mr-2" />
                              )}
                              <span>Run Test</span>
                            </Button>
                          </div>
                        </div>

                      </Grid>

                      {/* test result */}
                      {showSideInfo && (
                        <Grid item xs={6} className="d-flex flex-column">
                          <div
                            style={{ maxHeight: '65vh', overflow: 'auto' }}
                            className="flex-fill"
                          >
                            <TestResult data={submission.test} />
                          </div>

                          <div className="mt-3 d-flex align-items-center justify-content-end">
                            <Button
                              type="button"
                              color="primary"
                              variant="outlined"
                              onClick={() => {
                                updateSubmission((submission) => {
                                  submission.test.outputs = null;
                                  submission.test.errorMessage = '';
                                  return submission;
                                });
                              }}
                            >
                              Close
                            </Button>
                          </div>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                );
              }

              return '';
            })()}
          </Grid>
        </Container>
      </Backdrop>
    </>
  );
};
